import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, CheckCircle, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { apiClient } from '../../api/client';

const CATEGORY_COLORS = {
  health: 'from-pink-500 to-rose-500',
  wealth: 'from-blue-500 to-indigo-500',
  happiness: 'from-amber-500 to-orange-500',
};

const RealWorldChallengeCard = () => {
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [challengeRes, statsRes] = await Promise.all([
          apiClient.getTodaysChallenge(),
          apiClient.getChallengeStats(),
        ]);
        setChallenge(challengeRes.challenge);
        setStats(statsRes);
      } catch (err) {
        console.error('Failed to load challenge:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleComplete = async () => {
    if (!challenge || challenge.completed) return;
    setCompleting(true);
    try {
      await apiClient.logChallenge({
        action_type: 'other',
        description: challenge.description,
        category: challenge.category,
        from_daily_challenge: true,
        daily_challenge_title: challenge.title,
      });
      setChallenge(prev => ({ ...prev, completed: true }));
      setJustCompleted(true);
      // Refresh stats
      const statsRes = await apiClient.getChallengeStats();
      setStats(statsRes);
    } catch (err) {
      console.error('Failed to complete challenge:', err);
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="real-world-challenge-card">
        <div className="card-header">
          <h2 className="card-title">Today&apos;s Challenge</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  const gradient = CATEGORY_COLORS[challenge?.category] || CATEGORY_COLORS.health;

  return (
    <div className="card" data-testid="real-world-challenge-card">
      <div className="card-header">
        <h2 className="card-title">Today&apos;s Challenge</h2>
        <button
          onClick={() => navigate('/challenges')}
          className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
          data-testid="view-all-challenges"
        >
          View All <ArrowRight size={12} />
        </button>
      </div>

      {/* Challenge Content */}
      <div className={`p-4 rounded-xl bg-gradient-to-r ${gradient} text-white mb-4`}>
        <div className="flex items-center gap-2 mb-2">
          <Target size={16} />
          <span className="text-xs font-bold uppercase tracking-wider opacity-80">Real-World Goal</span>
        </div>
        <h3 className="font-bold text-lg mb-1">{challenge?.title}</h3>
        <p className="text-sm opacity-90">{challenge?.description}</p>
      </div>

      {/* Complete Button */}
      {challenge?.completed || justCompleted ? (
        <div className="flex items-center gap-2 text-green-600 font-semibold text-sm py-2" data-testid="challenge-completed-status">
          <CheckCircle size={18} />
          <span>Challenge completed today!</span>
          {justCompleted && <Sparkles size={14} className="text-amber-500" />}
        </div>
      ) : (
        <button
          onClick={handleComplete}
          disabled={completing}
          className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="complete-challenge-btn"
        >
          {completing ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
          I Did This!
        </button>
      )}

      {/* Mini Stats */}
      {stats && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <span>{stats.real_world_streak} day streak</span>
          <span>{stats.week_count} this week</span>
          <span>{stats.total_xp} XP earned</span>
        </div>
      )}
    </div>
  );
};

export default RealWorldChallengeCard;
